"use client";
import { cn } from "@pulsex/utils";
import Link from "next/link";
import { Category } from "@/ui/Category/Index";
import Author from "@/ui/Author/Index";
import ImageUI from "@/ui/Image/Index";
import { Summary } from "@/ui/Summary/Index";
import { MediaIcon } from "@/ui/MediaIcon/Index";
import { getNewspaperDate } from "@/utils/getNewspaperDate";
import { formatArabicDate } from "@/utils/formatDate";
import { useMemo } from "react";
import { Time } from "@/ui/Time/Index";
import { usePathname } from "next/navigation";
import { MAIN_TERM_PATHS_ARRAY } from "@/constants";
import { postHref } from "@/utils/links";

const sliceURL = (url: string) =>
  url?.startsWith("http://alpha.al-akhbar.com")
    ? url.slice("http://alpha.al-akhbar.com".length)
    : url || "";

interface CardProps {
  className?: string;
  showSummary?: boolean;
  showImage?: boolean;
  showAuthor?: boolean;
  showCategory?: boolean;
  customBackground?: string;
  showMediaIcon?: boolean;
  customImage?: string;
  customTitle?: string;
  mediatype?: string;
  data?: any;
  customCategory?: string;
  customTime?: string;
  showTime?: boolean;
  customSummary?: string;
  customAuthor?: string;
  authorTheme?: string;
  cropSize?: string;
  resolution?: number;
  isMobile?: any;
  customMetaFlex?: any;
  widgetColor?: any;
  widgetTitle?: any;
  isSearch?: any;
  template?: string;
  fixedSummary?: boolean;
  forcePal?: boolean;
}

export const Card = ({
  showSummary = true,
  showCategory = true,
  showAuthor = true,
  showImage = true,
  showMediaIcon = false,
  className,
  customBackground,
  customImage,
  customTitle,
  data,
  customCategory,
  customTime,
  showTime = true,
  customSummary,
  customAuthor,
  cropSize = "wide",
  resolution,
  mediatype,
  customMetaFlex,
  widgetColor,
  widgetTitle,
  isSearch = false,
  template = "text-overlay",
  fixedSummary = false,
  forcePal = false,
}: CardProps) => {
  const dateValue = getNewspaperDate(data);
  const isUrgentNews = data?.postType === "أخبار عاجلة";

  const TermValue =
    data?.terms?.[0]?.title === "على الغلاف"
      ? data?.terms?.[1]
      : data?.terms?.[0];

  const CategoryValue =
    data?.tag?.title !== widgetTitle?.trim() ? data?.tag : TermValue;

  const cardUrl = postHref(data, "/");
  const animatorMeta = data?.metas?.find(
    (item: any) => item.key === "animator" && item.value
  );

  const showAuthorBlock =
    showAuthor && !animatorMeta && data?.postType !== "opinion-caricature";

  const useMainTerm = (pathname: string) => {
    return MAIN_TERM_PATHS_ARRAY.indexOf(pathname) !== -1;
  };

  const pathname = usePathname();
  const decodedPathname = useMemo(() => decodeURI(pathname), [pathname]);
  const isMainTerm = useMainTerm(decodedPathname);

  const terms = data?.terms ?? [];
  const forceTerm = (() => {
    if (terms[0]?.title === "على الغلاف") {
      return terms[2];
    }

    if (
      terms.length > 2 &&
      ((terms[0]?.title === "عرب" &&
        terms[1]?.title !== "فلسطين" &&
        terms[1]?.title !== "سوريا") ||
        terms[0]?.title === "عالم")
    ) {
      return terms[2];
    }

    if (terms[0]?.title === "سوريا") {
      return terms[0];
    }

    if (terms.length < 3 && terms[1]?.title === "حياة وناس") {
      return terms[0];
    }

    return terms[1] ?? terms[0];
  })();

  const renderImage = () => (
    <div className={cn("overflow-hidden relative w-full", customImage)}>
      <ImageUI
        data={data}
        cropSize={cropSize}
        resolution={resolution}
        className="rounded-none w-full bg-greyBackgroundDim/60"
      />
    </div>
  );

  const renderMediaIcon = () => {
    const iconType =
      showMediaIcon && data?.postType === "video"
        ? "video"
        : showMediaIcon
        ? "camera"
        : undefined;
    return (
      <MediaIcon
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] max-md:h-10 max-md:w-10 h-16 w-16 fill-masterRed"
        type={iconType}
        iconClassName="fill-masterRed"
      />
    );
  };

  const dateToShow = useMemo(() => {
    try {
      let date: string | undefined;
      if (data?.publishDate && !isSearch && !dateValue) {
        date = formatArabicDate({
          arabicDate: data.publishDate,
          needDay: false,
        });
      } else if (isSearch && data?.displayDate) {
        date = formatArabicDate({
          arabicDate: data.displayDate,
          needDay: false,
        });
      } else if (data?.publishDate && !isSearch && dateValue) {
        date = formatArabicDate({
          arabicDate: dateValue || data.publishDate,
          needDay: false,
        });
      }
      return date;
    } catch (error) {
      console.error("Failed to format date:", error);
      return dateValue || data?.displayDate || data?.publishDate;
    }
  }, [data, isSearch, dateValue]);

  var termsCheck = data && Array.isArray(data?.terms) ? data?.terms : [];
  var palestineTerm = undefined;

  for (var i = 0; i < termsCheck.length; i++) {
    if (termsCheck[i]?.title && termsCheck[i]?.title.indexOf("فلسطين") !== -1) {
      palestineTerm = termsCheck[i];
      break;
    }
  }

  if (!data) return null;

  return (
    <div
      className={cn(
        "flex relative group overflow-hidden bg-lightgreyBackground rounded-t-3xl flex-col pb-2",
        "hover:lg:bg-[#f9f9f9] hover:lg:shadow transition-all",
        className
      )}
    >
      {showImage &&
        (isUrgentNews ? (
          renderImage()
        ) : (
          <div className="relative">
            <Link
              href={isSearch ? sliceURL(cardUrl) : cardUrl}
              aria-label={data?.title}
              prefetch={false}
              className={cn(
                "block overflow-hidden relative cursor-pointer w-full",
                template === "text-overlay" && "smooth-image-gradient",
                customImage
              )}
            >
              {showMediaIcon && renderMediaIcon()}

              <ImageUI
                data={data}
                cropSize={cropSize}
                resolution={resolution}
                className="rounded-none w-full"
              />
            </Link>
            {template === "text-overlay" && (
              <div className="absolute bottom-3 right-0 z-[2] pr-3 max-w-[96%]">
                {showCategory && CategoryValue && (
                  <Category
                    color={widgetColor || data?.tag?.color}
                    item={
                      forcePal && palestineTerm
                        ? palestineTerm
                        : (isMainTerm || fixedSummary) && forceTerm
                        ? forceTerm
                        : CategoryValue
                    }
                    className={cn(customCategory)}
                    paintBg
                  />
                )}
                <h3
                  className={cn(
                    "cursor-pointer line-clamp-2 group- text-white max-w-full",
                    "text-2xl lg:text-3xl",
                    customTitle
                  )}
                >
                  <Link href={isSearch ? sliceURL(cardUrl) : cardUrl} prefetch={false}>
                    {data?.title}
                  </Link>
                </h3>
              </div>
            )}
          </div>
        ))}

      <div
        className={cn(
          "flex flex-col items-start max-sm:ml-0 flex-1",
          !showImage && "rtl:md:mr-0 ltr:md:ml-0 w-full",
          customBackground
        )}
      >
        <div
          className={cn(
            "h-full w-full flex flex-col mt-0",
            template !== "text-overlay"
              ? "p-2.5 max-md:p-3 max-sm:px-2"
              : "p-2 max-md:p-1.5 lg:px-3"
          )}
        >
          {template !== "text-overlay" && showCategory && CategoryValue && (
            <Category
              color={widgetColor || data?.tag?.color}
              item={
                forcePal && palestineTerm
                  ? palestineTerm
                  : (isMainTerm || fixedSummary) && forceTerm
                  ? forceTerm
                  : CategoryValue
              }
              className={cn(customCategory)}
              paintBg
            />
          )}

          {template !== "text-overlay" && (
            <Link href={isSearch ? sliceURL(cardUrl) : cardUrl} prefetch={false}>
              <h2
                className={cn(
                  "cursor-pointer line-clamp-2 text-black",
                  "max-[360px]:text-[20px] text-[22px] max-md:leading-7 md:text-[26px] pl-1.5",
                  template !== "text-overlay" && "pl-0",
                  customTitle
                )}
              >
                {data?.title}
              </h2>
            </Link>
          )}

          {fixedSummary
            ? showSummary && (
                <Summary
                  className={cn(
                    "max-w-full max-md:text-sm font-noto-arabic text-base",
                    "md:line-clamp-2 line-clamp-2",
                    "text-[#545454]",
                    customSummary
                  )}
                >
                  {data?.summary || data?.textContent}
                </Summary>
              )
            : showSummary &&
              (data?.summary || data?.textContent) && (
                <Summary
                  className={cn(
                    "max-w-full max-md:text-sm font-noto-arabic text-base",
                    "md:line-clamp-2 line-clamp-2",
                    "text-[#545454]",
                    customSummary
                  )}
                >
                  {data?.summary || data?.textContent}
                </Summary>
              )}

          <div
            className={cn(
              "flex space-x-1 space-x-reverse items-center justify-between w-full h-fit mt-auto pt-2",
              customMetaFlex
            )}
          >
            {showAuthorBlock && (
              <Author post={data} className={cn("", customAuthor)} />
            )}

            {data?.postType === "opinion-caricature" && animatorMeta && (
              <div
                className={cn("text-sm w-fit cursor-pointer text-minorTheme")}
              >
                {animatorMeta.value}
              </div>
            )}

            {showTime && dateToShow && (
              <Time item={dateToShow} className={cn("mr-auto", customTime)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
